import React, { useEffect } from 'react'
import { Modal, ModalModel, SkeletonElement } from '@notino/react-styleguide'
import { google, outlook, office365, msTeams, yahoo, aol, outlookMobile, office365Mobile } from 'calendar-link'
import * as ics from 'ics'
import dayjs from 'dayjs'
import * as Sentry from '@sentry/react'
import { Portal } from 'react-portal'

// utils
import { isMobileDevice, showNotifications } from '../../utils/helper'
import { ICS_DATE_FORMAT, MSG_TYPE } from '../../utils/enums'
import { getIntl } from '../../utils/intl'

// assets
import GoogleCalendarIcon from '../../assets/icons/GoogleCalendarIcon'
import Office365Icon from '../../assets/icons/Office365Icon'
import OutlookIcon from '../../assets/icons/OutlookIcon'
import MsTeamsIcon from '../../assets/icons/MsTeamsIcon'
import YahooIcon from '../../assets/icons/YahooIcon'
import CalendarIcon from '../../assets/icons/CalendarIcon'
import AppleIcon from '../../assets/icons/AppleIcon'

// hooks
import useMessages from '../../hooks/useMessages'

// styles
import * as SC from './AddToCalendarModalStyles'

type CalendarFile = {
	/** Format: date-time */
	start: string
	/** Format: date-time */
	end: string
	UID: string
	description: string
	summary: string
}

type Props = {
	show: boolean
	onClose: () => void
	calendarFile: CalendarFile
	reservationID: string
}

type CalendarLinkItemType = {
	type: 'link'
	link: string | undefined
}

type CalendarButtonItemType = {
	type: 'button'
	onClick: () => void
}

type CalendarItem = {
	title: string
	icon: React.ReactNode
} & (CalendarLinkItemType | CalendarButtonItemType)

const onDownloadIcsFile = async (calendarFile: CalendarFile, reservationID: string) => {
	const intl = getIntl()
	const filename = `reservation-${calendarFile.UID}.ics`

	try {
		const file = await new Promise<File>((resolve, reject) => {
			ics.createEvent(
				{
					title: calendarFile.description,
					description: calendarFile.summary,
					start: dayjs(calendarFile.start).utc().format(ICS_DATE_FORMAT),
					end: dayjs(calendarFile.end).utc().format(ICS_DATE_FORMAT),
					uid: calendarFile.UID
				},
				(error, value) => {
					if (error) {
						reject(error)
					}

					resolve(new File([value], filename, { type: 'text/calendar' }))
				}
			)
		})
		const url = URL.createObjectURL(file)

		// trying to assign the file URL to a window could cause cross-site
		// issues so this is a workaround using HTML5
		const anchor = document.createElement('a')
		anchor.href = url
		anchor.download = filename

		document.body.appendChild(anchor)
		anchor.click()
		document.body.removeChild(anchor)

		URL.revokeObjectURL(url)
	} catch (error) {
		showNotifications([{ type: MSG_TYPE.ERROR, message: intl.formatMessage({ id: 'We could not create your calendar event. Please try again.' }) }])
		// eslint-disable-next-line no-console
		Sentry.captureException(new Error(`Error creating calendar event .ics file with an ID ${reservationID}`, { cause: error }))
	}
}

const getCalendarLink = (calendarFile: CalendarFile, calendarType: 'google' | 'outlook' | 'office365' | 'yahoo' | 'aol' | 'msTeams'): string | undefined => {
	const calendarEvent = {
		title: calendarFile.description,
		description: calendarFile.summary,
		start: calendarFile.start,
		end: calendarFile.end
	}

	let link: string | undefined

	switch (calendarType) {
		case 'google':
			link = google(calendarEvent)
			break
		case 'outlook':
			link = isMobileDevice() ? outlookMobile(calendarEvent) : outlook(calendarEvent)
			break
		case 'office365':
			link = isMobileDevice() ? office365Mobile(calendarEvent) : office365(calendarEvent)
			break
		case 'yahoo':
			link = yahoo(calendarEvent)
			break
		case 'aol':
			link = aol(calendarEvent)
			break
		case 'msTeams':
			link = msTeams(calendarEvent)
			break
		default:
			break
	}

	return link
}

const AddToCalendarModal = (props: Props) => {
	const { show, onClose, calendarFile, reservationID } = props
	const { messages } = useMessages()

	const [isLoading, setIsLoading] = React.useState(true)

	/**
	 * Loading because this is just a client side Modal component, where we are working with window object which is not available during SSR.
	 */
	useEffect(() => {
		setIsLoading(false)
	}, [])

	const calendarItems: CalendarItem[] = [
		{
			type: 'button',
			title: 'Apple',
			icon: <AppleIcon />,
			onClick: () => onDownloadIcsFile(calendarFile, reservationID)
		},
		{
			type: 'link',
			title: 'Google',
			icon: <GoogleCalendarIcon />,
			link: getCalendarLink(calendarFile, 'google')
		},
		{
			type: 'link',
			title: 'Microsoft Office 365',
			icon: <Office365Icon />,
			link: getCalendarLink(calendarFile, 'office365')
		},
		{
			type: 'link',
			title: 'Outlook',
			icon: <OutlookIcon />,
			link: getCalendarLink(calendarFile, 'outlook')
		},
		{
			type: 'link',
			title: 'Microsoft Teams',
			icon: <MsTeamsIcon />,
			link: getCalendarLink(calendarFile, 'msTeams')
		},
		{
			type: 'link',
			title: 'Yahoo',
			icon: <YahooIcon />,
			link: getCalendarLink(calendarFile, 'yahoo')
		},
		{
			type: 'button',
			title: messages['Download .ics file'],
			icon: <CalendarIcon />,
			onClick: () => onDownloadIcsFile(calendarFile, reservationID)
		}
	]

	const loadingSkeletons = Array.from({ length: calendarItems.length }, (_, index) => (
		<SC.ElementsListItem key={index}>
			<SC.ElementsListItemSkeleton>
				<SC.SkeletonIconElement width={'16px'} height={'16px'} />
				<SkeletonElement width='100%' height='20px' />
			</SC.ElementsListItemSkeleton>
		</SC.ElementsListItem>
	))

	return (
		<Portal>
			<Modal show={show} onClose={onClose} size={ModalModel.ModalSizes.small} noBorders centerVertically>
				<SC.ModalHeader>{messages['Add to calendar']}</SC.ModalHeader>
				<SC.ModalMain>
					<SC.ElementsList>
						{isLoading
							? loadingSkeletons
							: calendarItems.map((item, index) => {
									if (item.type === 'link') {
										return (
											<SC.ElementsListItem key={index}>
												<SC.ElementsListItemLink
													href={item.link}
													target='_blank'
													rel='noopener noreferrer'
													onClick={() => {
														if (!item.link) {
															showNotifications([
																{
																	type: MSG_TYPE.ERROR,
																	message: messages['We could not create your calendar event. Please try again.']
																}
															])
															Sentry.captureException(
																new Error(`Error creating calendar event .ics file with an ID ${reservationID}`)
															)
														}
													}}
												>
													{item.icon} {item.title}
												</SC.ElementsListItemLink>
											</SC.ElementsListItem>
										)
									}

									return (
										<SC.ElementsListItem key={index}>
											<SC.ElementsListItemButton onClick={item.onClick}>
												{item.icon} {item.title}
											</SC.ElementsListItemButton>
										</SC.ElementsListItem>
									)
								})}
					</SC.ElementsList>
				</SC.ModalMain>
			</Modal>
		</Portal>
	)
}

export default AddToCalendarModal
